import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaPlus, FaEdit, FaTrash, FaEye, FaSearch, FaFilter,
  FaBlog, FaCheck, FaChartBar, FaLinkedin
} from 'react-icons/fa';
import { blogsApi, linkedinApi } from '../../services/api';
import { AdminLayout } from '../../components/admin';

const BlogsManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, published: 0, drafts: 0 });
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ status: '', search: '', category: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [categories, setCategories] = useState([]);
  const [linkedinStatus, setLinkedinStatus] = useState({ connected: false });
  const [postingToLinkedIn, setPostingToLinkedIn] = useState(null);
  const [linkedinMessage, setLinkedinMessage] = useState({ type: '', text: '' });

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await blogsApi.getAll(
        pagination.page,
        10,
        filters.status,
        filters.search,
        filters.category
      );
      if (response.success) {
        setBlogs(response.data || []);
        setPagination(prev => ({
          ...prev,
          totalPages: response.pagination?.total_pages || 1
        }));
      }
    } catch (err) {
      console.error('Failed to fetch blogs:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, filters.status, filters.search, filters.category]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await blogsApi.getCategories();
      if (response.success && response.data?.categories) {
        setCategories(response.data.categories);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await blogsApi.getAll(1, 1000);
      if (response.success) {
        const allBlogs = response.data || [];
        const published = allBlogs.filter(b => b.status === 'published').length;
        setStats({
          total: allBlogs.length,
          published,
          drafts: allBlogs.length - published
        });
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const checkLinkedInStatus = useCallback(async () => {
    try {
      const response = await linkedinApi.getStatus();
      if (response.success) {
        setLinkedinStatus(response.data);
      }
    } catch (err) {
      console.error('Failed to check LinkedIn status:', err);
    }
  }, []);

  const handlePostToLinkedIn = async (blog) => {
    if (!linkedinStatus.connected) {
      setLinkedinMessage({ type: 'error', text: 'Please connect your LinkedIn account first in Settings' });
      setTimeout(() => setLinkedinMessage({ type: '', text: '' }), 5000);
      return;
    }

    setPostingToLinkedIn(blog.id);
    setLinkedinMessage({ type: '', text: '' });

    try {
      const response = await linkedinApi.post(blog.id, null, true);
      if (response.success) {
        setLinkedinMessage({ type: 'success', text: `"${blog.title}" posted to LinkedIn successfully!` });
      } else {
        setLinkedinMessage({ type: 'error', text: response.message || 'Failed to post to LinkedIn' });
      }
    } catch (err) {
      setLinkedinMessage({ type: 'error', text: err.message || 'Failed to post to LinkedIn' });
    } finally {
      setPostingToLinkedIn(null);
      setTimeout(() => setLinkedinMessage({ type: '', text: '' }), 5000);
    }
  };

  useEffect(() => {
    fetchBlogs();
    fetchStats();
    fetchCategories();
    checkLinkedInStatus();
  }, [fetchBlogs, fetchStats, fetchCategories, checkLinkedInStatus]);

  const handleDelete = async (id) => {
    try {
      const response = await blogsApi.delete(id);
      if (response.success) {
        setDeleteConfirm(null);
        fetchBlogs();
        fetchStats();
      }
    } catch (err) {
      console.error('Failed to delete blog:', err);
    }
  };

  const togglePublish = async (blog) => {
    try {
      const newStatus = blog.status === 'published' ? 'draft' : 'published';
      const response = await blogsApi.update(blog.id, { status: newStatus });
      if (response.success) {
        fetchBlogs();
        fetchStats();
      }
    } catch (err) {
      console.error('Failed to update blog:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-dark-800">Blog Management</h1>
            <p className="text-dark-500 mt-1">Create and manage your blog posts</p>
          </div>
          <Link
            to="/admin/blogs/new"
            className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <FaPlus />
            <span>New Post</span>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-500 text-sm">Total Posts</p>
                <p className="text-2xl font-bold text-dark-800">{stats.total}</p>
              </div>
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <FaBlog className="w-5 h-5 text-primary-500" />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-500 text-sm">Published</p>
                <p className="text-2xl font-bold text-green-600">{stats.published}</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <FaCheck className="w-5 h-5 text-green-500" />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-500 text-sm">Drafts</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.drafts}</p>
              </div>
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <FaChartBar className="w-5 h-5 text-yellow-500" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* LinkedIn Status Message */}
        {linkedinMessage.text && (
          <motion.div
            className={`px-4 py-3 rounded-lg flex items-center justify-between ${
              linkedinMessage.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center space-x-2">
              <FaLinkedin className={linkedinMessage.type === 'success' ? 'text-green-600' : 'text-red-600'} />
              <span>{linkedinMessage.text}</span>
            </div>
            <button onClick={() => setLinkedinMessage({ type: '', text: '' })} className="text-lg">&times;</button>
          </motion.div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400" />
            <input
              type="text"
              placeholder="Search blogs..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400" />
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </div>
          <div className="relative">
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Blogs Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
            </div>
          ) : blogs.length === 0 ? (
            <div className="p-12 text-center">
              <FaBlog className="w-12 h-12 text-dark-300 mx-auto mb-4" />
              <p className="text-dark-500 mb-4">No blog posts found</p>
              <Link
                to="/admin/blogs/new"
                className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                <FaPlus />
                <span>Create First Post</span>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-dark-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-dark-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-dark-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-dark-500 uppercase tracking-wider">
                      Views
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-dark-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-dark-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {blogs.map((blog) => (
                    <tr key={blog.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          {blog.featured_image && (
                            <img
                              src={blog.featured_image}
                              alt=""
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          )}
                          <div className="max-w-xs">
                            <p className="font-medium text-dark-800 truncate">{blog.title}</p>
                            <p className="text-sm text-dark-400 truncate">{blog.excerpt}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {blog.category_name ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                            {blog.category_name}
                          </span>
                        ) : (
                          <span className="text-dark-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => togglePublish(blog)}
                          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                            blog.status === 'published'
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          }`}
                        >
                          {blog.status === 'published' ? 'Published' : 'Draft'}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-dark-500">
                        {blog.views || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-dark-500">
                        {formatDate(blog.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end space-x-2">
                          {blog.status === 'published' && (
                            <>
                              <button
                                onClick={() => handlePostToLinkedIn(blog)}
                                disabled={postingToLinkedIn === blog.id}
                                className={`p-2 transition-colors ${
                                  postingToLinkedIn === blog.id
                                    ? 'text-blue-400 cursor-wait'
                                    : 'text-dark-400 hover:text-blue-600'
                                }`}
                                title="Post to LinkedIn"
                              >
                                {postingToLinkedIn === blog.id ? (
                                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <FaLinkedin />
                                )}
                              </button>
                              <Link
                                to={`/blogs/${blog.slug}`}
                                target="_blank"
                                className="p-2 text-dark-400 hover:text-primary-500 transition-colors"
                                title="View"
                              >
                                <FaEye />
                              </Link>
                            </>
                          )}
                          <Link
                            to={`/admin/blogs/edit/${blog.id}`}
                            className="p-2 text-dark-400 hover:text-primary-500 transition-colors"
                            title="Edit"
                          >
                            <FaEdit />
                          </Link>
                          <button
                            onClick={() => setDeleteConfirm(blog.id)}
                            className="p-2 text-dark-400 hover:text-red-500 transition-colors"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
              disabled={pagination.page === 1}
              className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-dark-600">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination(p => ({ ...p, page: Math.min(p.totalPages, p.page + 1) }))}
              disabled={pagination.page === pagination.totalPages}
              className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-white rounded-xl p-6 max-w-md w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 className="text-lg font-bold text-dark-800 mb-4">Delete Blog Post?</h3>
            <p className="text-dark-500 mb-6">
              Are you sure you want to delete this blog post? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-dark-600 hover:text-dark-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AdminLayout>
  );
};

export default BlogsManagement;
